<script lang="ts">
	/**
	 * ExhibitEncounter — the full encounter → EHR pipeline.
	 *
	 * Beat 04: Coding and claims, in one pass.
	 * Pain anchor: ~$150K/yr lost to undercoding for a $3M practice
	 *              (Cosentus 2026); 36 minutes in EHR per 30-min visit
	 *              (AMA 2024); 1.2 hrs/day pajama time on documentation
	 *              and inbox (AMA 2024).
	 *
	 * Visual hierarchy (top → bottom):
	 *   1. Scribe note quote — "from your scribe · via Abridge"
	 *   2. Extracted codes — ICD-10/CPT/modifier with biller-approval state
	 *   3. EHR updates posted — concrete actions that hit the chart
	 *   4. Per-encounter revenue impact — closing line in sage
	 *
	 * Brand discipline: codes are surfaced for the biller, not auto-applied.
	 * EHR updates are administrative-only (encounter note, charges, follow-up
	 * tasks). Clinical sign-off remains with the physician.
	 */

	import EkaviMark from '../EkaviMark.svelte';
</script>

<figure
	class="exhibit"
	aria-label="Encounter pipeline showing scribe note, extracted codes for biller approval, and EHR updates posted"
>
	<!-- 1. Scribe note (top) -->
	<div class="head">
		<span class="eyebrow">From your scribe · Wong, J. · 24m visit</span>
	</div>

	<blockquote class="note">
		"Follow-up post-arthroscopy. Joint injection under fluoro. PT plan, MRI if pain persists."
	</blockquote>

	<!-- 2. Extracted codes -->
	<p class="section-eyebrow">Codes Ekavi extracted</p>
	<ul class="codes">
		<li class="code">
			<span class="rail" aria-hidden="true">
				<EkaviMark size={12} decorative maskIdSuffix="enc-c1" />
			</span>
			<span class="code-id">99213</span>
			<span class="code-desc">E&M established · level 3</span>
			<span class="code-state state-queued">queued</span>
		</li>
		<li class="code">
			<span class="rail" aria-hidden="true">
				<EkaviMark size={12} decorative maskIdSuffix="enc-c2" />
			</span>
			<span class="code-id">20610</span>
			<span class="code-desc">Joint injection · major</span>
			<span class="code-state state-queued">queued</span>
		</li>
		<li class="code">
			<span class="rail" aria-hidden="true">
				<EkaviMark size={12} decorative maskIdSuffix="enc-c3" />
			</span>
			<span class="code-id">+ mod 25</span>
			<span class="code-desc">E&M with procedure same day</span>
			<span class="code-state state-review">needs review</span>
		</li>
	</ul>

	<!-- 3. EHR updates posted -->
	<p class="section-eyebrow">Posted to EHR</p>
	<ul class="updates">
		<li class="update">
			<span class="check" aria-hidden="true">✓</span>
			<span class="update-text">Encounter note filed</span>
		</li>
		<li class="update">
			<span class="check" aria-hidden="true">✓</span>
			<span class="update-text">Charges queued for claim</span>
		</li>
		<li class="update">
			<span class="check" aria-hidden="true">✓</span>
			<span class="update-text">MRI follow-up routed to MA</span>
		</li>
	</ul>

</figure>

<style>
	.exhibit {
		background: var(--ekavi-paper);
		border: var(--ekavi-stroke-hairline);
		padding: var(--ekavi-space-6);
		margin: 0;
	}

	.head {
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		gap: var(--ekavi-space-3);
		padding-bottom: var(--ekavi-space-3);
		border-bottom: var(--ekavi-stroke-hairline);
		margin-bottom: var(--ekavi-space-4);
	}
	.eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
	}
	.src {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		color: var(--ekavi-ink-3);
	}

	.note {
		margin: 0 0 var(--ekavi-space-6);
		padding: var(--ekavi-space-3) var(--ekavi-space-4);
		background: var(--ekavi-bone);
		border-left: 2px solid var(--ekavi-charcoal);
		font-family: var(--ekavi-font-ui);
		font-style: italic;
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
		line-height: 1.55;
	}

	.section-eyebrow {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		letter-spacing: var(--ekavi-track-caps);
		text-transform: uppercase;
		color: var(--ekavi-ink-3);
		font-weight: var(--ekavi-weight-halbfett);
		margin: 0 0 var(--ekavi-space-3);
	}

	.codes,
	.updates {
		list-style: none;
		padding: 0;
		margin: 0 0 var(--ekavi-space-6);
	}

	.code {
		display: grid;
		grid-template-columns: 16px 88px minmax(0, 1fr) auto;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: var(--ekavi-space-2);
		border-bottom: var(--ekavi-stroke-hairline);
	}
	.code:last-child {
		border-bottom: none;
	}
	.rail {
		display: inline-flex;
		justify-content: center;
		color: var(--ekavi-charcoal);
	}
	.code-id {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-14);
		font-weight: var(--ekavi-weight-halbfett);
		color: var(--ekavi-ink-1);
		font-variant-numeric: tabular-nums;
	}
	.code-desc {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-2);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.code-state {
		font-family: var(--ekavi-font-mono);
		font-size: var(--ekavi-text-12);
		text-transform: lowercase;
	}
	.state-queued {
		color: var(--ekavi-sage);
	}
	.state-review {
		color: var(--ekavi-ochre);
		font-weight: var(--ekavi-weight-halbfett);
	}

	.update {
		display: grid;
		grid-template-columns: 16px 1fr;
		align-items: center;
		gap: var(--ekavi-space-3);
		padding-block: 3px;
	}
	.check {
		font-family: var(--ekavi-font-mono);
		color: var(--ekavi-sage);
		font-weight: var(--ekavi-weight-kraftig);
		text-align: center;
	}
	.update-text {
		font-family: var(--ekavi-font-ui);
		font-size: var(--ekavi-text-14);
		color: var(--ekavi-ink-1);
	}


	@media (max-width: 480px) {
		.code {
			grid-template-columns: 16px 1fr auto;
		}
		.code-desc {
			grid-column: 2 / -1;
			grid-row: 2;
			margin-top: 2px;
		}
	}
</style>
